import { Component, Inject, OnInit } from '@angular/core';
import { AsyncPipe, DatePipe, DOCUMENT } from '@angular/common';
import { BehaviorSubject, merge, Observable, of } from 'rxjs';
import { catchError, map, share, switchMap, tap } from 'rxjs/operators';
import { FetchArgs, PagingToken } from 'src/app/shared/models/core';
import { QuickSearchCandidateGrid } from 'src/app/search-all-candidates/models/quicksearch-candidate-grid.model';
import { CandidateGridResponse } from 'src/app/recruiting/candidates/grid/models/candidate-grid-response';
import { RecruitingService } from 'src/app/recruiting/recruiting.service';
import { IdentityService } from 'src/app/core/identity.service';
import { groupBy, GroupResult, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { FollowupTime } from 'src/app/shared/models/followup-time.model';
import { CandidateAssignment } from 'src/app/shared/models/enums/candidate-assignment.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { GridComponent } from 'src/app/shared/grid/grid.component';
import { GridColumnDirective } from 'src/app/shared/grid/grid-column.directive';
import { PhonePipe } from 'src/app/shared/pipes/phone.pipe';
import { MatIconModule } from '@angular/material/icon';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';

const DEFAULT_PAGE_SIZE = 50;

@UntilDestroy()
@Component({
    selector: 'nova-candidate-search-grid',
    standalone: true,
    imports: [GridComponent, GridColumnDirective, AsyncPipe, PhonePipe, DatePipe, MatIconModule],
    templateUrl: './candidate-search-grid.component.html',
    styleUrls: ['./candidate-search-grid.component.scss']
})
export class CandidateSearchGridComponent implements OnInit {
    data$: Observable<PagingToken<GroupResult[] | QuickSearchCandidateGrid[]>>;
    fetchArgs$: BehaviorSubject<FetchArgs>;
    isLoading$ = new BehaviorSubject<boolean>(false);
    count = 0;
    recruiterId = null;
    timeEntries: FollowupTime[] = [];
    filter = null;
    candidateAssignment = CandidateAssignment;
    showCandidateSearchModal = false;

    get defaultFetchArgs(): FetchArgs {
        return {
            filter: {},
            sort: { field: 'firstName', dir: 'asc' },
            skip: 0,
            pageSize: DEFAULT_PAGE_SIZE
        };
    }

    constructor(
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly recruitingService: RecruitingService,
        private readonly identityService: IdentityService,
        private readonly _featureManager: LDFeatureManager
    ) {
        this.recruiterId = identityService.current.contactId;
    }

    ngOnInit(): void {
        this._featureManager
            .isEnabled(FeatureFlag.CandidateSearchModal)
            .pipe(untilDestroyed(this))
            .subscribe((isEnabled) => (this.showCandidateSearchModal = isEnabled));
    }

    shouldHideContactInfo(dataItem: QuickSearchCandidateGrid): boolean {
        return dataItem?.isDeactivated === true;
    }

    quickSearchCandidates(filter) {
        this.filter = {
            ...filter,
            recruiterId: this.recruiterId
        };

        const fetchArgs = {
            ...this.defaultFetchArgs,
            filter: this.filter
        };

        if (!this.fetchArgs$) {
            this.fetchArgs$ = new BehaviorSubject<FetchArgs>(fetchArgs);
            this.getData();
        } else {
            this.fetchArgs$.next(fetchArgs);
        }
    }

    load(args: FetchArgs) {
        this.fetchArgs$.next({
            ...args,
            filter: this.filter
        });
    }

    openEmailClient(email: string) {
        this.document.location.href = 'mailto:' + email;
    }

    getAssignmentLabel(assignment: CandidateAssignment): string {
        switch (assignment) {
            case CandidateAssignment.Your:
                return 'Your Candidates';
            case CandidateAssignment.Others:
                return 'Other Candidates - Assigned';
            case CandidateAssignment.Unassigned:
                return 'Other Candidates - Unassigned';
            default:
                return 'Unknown Assignment';
        }
    }

    private getData() {
        this.data$ = merge(
            of({
                data: [],
                total: 0,
                totalWithoutFilter: 0
            }),
            this.fetchArgs$.pipe(
                untilDestroyed(this),
                tap((_) => this.isLoading$.next(true)),
                switchMap((args: FetchArgs) => {
                    return this.recruitingService.quickSearchCandidates(args);
                }),
                map((result) => {
                    const descriptor: SortDescriptor[] = [{ field: 'assignment', dir: 'asc' }];
                    return {
                        data: groupBy(orderBy(result.data, descriptor), descriptor),
                        total: result.total
                    };
                }),
                tap((resp) => {
                    this.count = resp?.total ?? 0;
                    this.isLoading$.next(false);
                }),
                catchError((_) => {
                    this.isLoading$.next(false);
                    return of(<CandidateGridResponse>{});
                }),
                share() //prevent re-fetching if more than one subscriber
            )
        );
    }
}
